// Deterministic (no-AI) command parser for AI Explore follow-ups.
//
// When a user types a follow-up that only changes HOW an existing result is
// presented (chart type, legend, colours, sort, top-N …) or a clearly
// mechanical re-query (date grain, row limit, add/remove a known field), we
// handle it locally instead of spending an LLM round-trip. Anything we can't
// confidently classify returns null → the caller falls through to the AI.

export const CHART_TYPE_WORDS = [
  { re: /\b(donut|doughnut)\b/, chartType: 'pie_chart', extra: { donut: true }, label: 'Donut chart' },
  { re: /\bpie\b/, chartType: 'pie_chart', extra: { donut: false }, label: 'Pie chart' },
  { re: /\b(line|trend)\s*(chart|graph)?\b/, chartType: 'line_chart', label: 'Line chart' },
  { re: /\barea\s*(chart|graph)?\b/, chartType: 'area_chart', label: 'Area chart' },
  { re: /\bscatter\s*(plot)?\b/, chartType: 'scatter', label: 'Scatter plot' },
  { re: /\bbubble\b/, chartType: 'bubble', label: 'Bubble chart' },
  { re: /\bcombo\b/, chartType: 'combo', label: 'Combo chart' },
  { re: /\b(tree\s*map)\b/, chartType: 'treemap', label: 'Treemap' },
  { re: /\bfunnel\b/, chartType: 'funnel', label: 'Funnel' },
  { re: /\bwaterfall\b/, chartType: 'waterfall', label: 'Waterfall' },
  { re: /\bheat\s*map\b/, chartType: 'heatmap', label: 'Heatmap' },
  { re: /\bgauge\b/, chartType: 'gauge', label: 'Gauge' },
  { re: /\bcolumn\s*(chart|graph)?\b/, chartType: 'bar_chart', extra: { orientation: 'vertical' }, label: 'Column chart' },
  { re: /\b(horizontal\s*bar|bar)\s*(chart|graph)?\b/, chartType: 'bar_chart', label: 'Bar chart' },
  { re: /\b(table|grid|list)\b/, outputType: 'table', label: 'Table' },
];

// A DISTINCTIVE "present the current result differently" signal: an explicit
// chart/graph/plot word, a chart-only word (pie/donut/scatter/…), or a table/grid
// target. Used to guarantee that a phrase like "change to a pie chart" is treated
// as a pure viz change — never as a field swap ("change <field> to <field>") that
// would re-plan the query and drop the measure's certified scope/filters.
export const VIZ_TARGET_RE = /\b(chart|graph|plot)\b|\b(pie|donut|doughnut|scatter|bubble|combo|funnel|waterfall|gauge|treemap|heatmap)\b|(tree\s*map|heat\s*map)|\b(as|to)\s+an?\s+(table|grid|list)\b/;

export const COLOR_WORDS = {
  blue: '#3b82f6', green: '#10b981', red: '#ef4444', orange: '#f59e0b',
  purple: '#8b5cf6', violet: '#8b5cf6', teal: '#14b8a6', pink: '#ec4899',
  yellow: '#eab308', cyan: '#06b6d4', indigo: '#6366f1',
};
export const PALETTE_WORDS = {
  warm: 'warm', cool: 'cool', earth: 'earth',
  monochrome: 'monochrome', grayscale: 'monochrome', greyscale: 'monochrome',
};
const GRAIN_WORDS = {
  day: 'day', daily: 'day', week: 'week', weekly: 'week',
  month: 'month', monthly: 'month', quarter: 'quarter', quarterly: 'quarter',
  year: 'year', yearly: 'year', annually: 'year', annual: 'year',
};

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

// Find a known field name inside the text (matched on a normalised form so
// "line of business" matches LINE_OF_BUSINESS_CODE-ish labels).
function matchField(text, fields) {
  const t = norm(text);
  let best = null;
  for (const f of fields || []) {
    // A field's own "identity" (name/labels) — used to break ties so that a
    // term like "state" prefers POLICY_STATE_CODE over another field that only
    // carries "state" as a loose synonym.
    const idText = norm([f.name, f.business_name, f.display_name, f.label].filter(Boolean).join(' '));
    const cands = [f.name, f.label, f.business_name, f.display_name, ...(f.synonyms || [])].filter(Boolean);
    for (const c of cands) {
      const n = norm(c);
      if (!n || n.length < 3) continue;
      // Match in EITHER direction: the candidate appears in the text
      // ("earned premium" → EARNED_PREMIUM) OR the typed term is contained in
      // the candidate ("line of business" → LINE_OF_BUSINESS_CODE). The second
      // direction is gated on a longer term to avoid noisy partial hits.
      let overlap = 0;
      if (t.includes(n)) overlap = n.length;
      else if (t.length >= 4 && n.includes(t)) overlap = t.length;
      if (overlap) {
        const score = overlap + (n === t ? 100 : 0) + (idText.includes(t) ? 25 : 0);
        if (!best || score > best.score) best = { field: f, score };
      }
    }
  }
  return best?.field || null;
}

// Return the substring that follows the first match of `kwRe`, cut off at the
// first match of `stopRe` (so "remove A and add B" scopes A to the remove part).
function segAfter(t, kwRe, stopRe) {
  const m = t.match(kwRe);
  if (!m) return null;
  let rest = t.slice(m.index + m[0].length);
  if (stopRe) { const s = rest.match(stopRe); if (s) rest = rest.slice(0, s.index); }
  return rest;
}

/**
 * Detect an explicit FIELD change (swap / replace / remove / add / break down by).
 * Returns a re-query command, the sentinel 'ROUTE_AI' when the intent is clearly a
 * field change we can't fully resolve (so the caller sends it to the AI instead of
 * mis-reading it as a chart tweak), or null when it isn't a field change at all.
 */
function parseFieldManipulation(t, ctx) {
  // A distinctive chart/table request is a PRESENTATION change, never a field
  // swap — bail so "change/switch to a <chart>" isn't read as "change A to B"
  // (which would re-plan the query via the AI and lose the certified scope).
  if (VIZ_TARGET_RE.test(t)) return null;
  const current = [...(ctx.currentDims || []), ...(ctx.currentMeasures || [])];
  const availMeasures = ctx.availableMeasures || [];
  const avail = [...(ctx.availableDims || []), ...availMeasures];
  const kindOf = (f) => availMeasures.some(m => m.name === f.name) ? 'measure' : 'dimension';

  // "swap/replace/change/switch A with/for/to B" — a structured field swap.
  const sw = t.match(/\b(swap|replace|change|switch)\b(.+?)\b(with|for|to|into|by)\b(.+)/);
  if (sw) {
    const aText = sw[2].trim(), bText = sw[4].trim();
    const a = matchField(sw[2], current) || matchField(sw[2], avail);
    const b = matchField(sw[4], avail);
    if (a && b && a.name !== b.name) {
      return { kind: 'requery', queryPatch: { removeField: a.name, addField: b.name, addKind: kindOf(b) },
        label: `Swapped ${a.label || a.name} → ${b.label || b.name}` };
    }
    // A field-swap was clearly intended but we couldn't resolve both sides
    // deterministically (e.g. the user typed "state" but the field is "Policy
    // State", or the term is ambiguous). Rather than refuse, re-ask the AI a
    // NATURAL rewrite of the original question so it can map the fuzzy term.
    if (a || b) {
      const orig = ctx.lastQuestion || '';
      if (orig && aText) {
        const re = new RegExp(aText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
        if (re.test(orig)) {
          return { kind: 'reask', prompt: orig.replace(re, bText), label: `Swapped ${aText} → ${bText}` };
        }
      }
      return 'ROUTE_AI';
    }
  }

  const patch = {};
  const labels = [];
  const remSeg = segAfter(t, /\b(remove|drop|exclude|without)\s+/, /\b(and|then|add|also|include|split|break|,)\b/);
  if (remSeg) {
    const f = matchField(remSeg, current);
    if (f) { patch.removeField = f.name; labels.push(`removed ${f.label || f.name}`); }
  }
  const addSeg = segAfter(t, /\b(add|also include|include|also|split by|break ?down by|and by)\s+/, /\b(then|,)\b/);
  if (addSeg) {
    const f = matchField(addSeg, avail);
    if (f && !current.some(c => c.name === f.name)) {
      const kind = kindOf(f);
      // Adding a MEASURE combines two facts' worth of aggregation — re-ask the
      // AI a natural rewrite so the query is planned correctly (robust to field
      // naming), mirroring the swap flow. Dimensions stay deterministic/instant.
      if (kind === 'measure' && !patch.removeField && (ctx.lastQuestion || '').trim()) {
        // Rebuild an explicit "M1 and M2 by D1 and D2" question so the AI keeps
        // the CURRENT grouping and returns both measures on one card (rather
        // than re-interpreting the added measure as its own trend).
        const mLabels = [...(ctx.currentMeasures || []).map(m => m.label || m.name), f.label || f.name];
        const dLabels = (ctx.currentDims || []).map(d => d.label || d.name);
        const prompt = dLabels.length ? `${mLabels.join(' and ')} by ${dLabels.join(' and ')}` : mLabels.join(' and ');
        return { kind: 'reask', prompt, label: `Added ${f.label || f.name}` };
      }
      patch.addField = f.name; patch.addKind = kind; labels.push(`added ${f.label || f.name}`);
    }
  }
  // "remove <field> and add <term>" where <term> isn't a known field → it's a
  // swap; re-ask a natural rewrite so the AI can map the fuzzy term. Gated on a
  // REAL current field being removed, so viz phrases ("remove legend") never hit this.
  if (patch.removeField && !patch.addField && addSeg && addSeg.trim()) {
    const orig = ctx.lastQuestion || '';
    const remText = (remSeg || '').trim();
    const addText = addSeg.trim();
    if (orig && remText) {
      const re = new RegExp(remText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
      if (re.test(orig)) return { kind: 'reask', prompt: orig.replace(re, addText), label: `Swapped ${remText} → ${addText}` };
    }
  }
  if (patch.removeField || patch.addField) {
    return { kind: 'requery', queryPatch: patch, label: labels.join(' · ') || 'Re-queried' };
  }
  return null;
}

/**
 * @param {string} text raw follow-up text
 * @param {object} ctx  { hasResult, hasDateDim, availableDims:[{name,label}], availableMeasures:[], currentDims:[], currentMeasures:[] }
 * @returns null | { kind:'viz', vizPatch, label } | { kind:'requery', queryPatch, label }
 */
export function parseVizCommand(text, ctx = {}) {
  if (!text || !ctx.hasResult) return null;
  const t = ` ${text.toLowerCase().trim()} `;

  // ── Series split / combine (colour-by) ────────────────────────────────
  // "two separate coloured lines", "one line per year", "colour by region",
  // "split by product", "break the bars out by LOB" → colour by a dimension.
  // "merge into one line", "stop splitting" → combine back.
  const chartType0 = ctx.currentChartType || '';
  const isPieLike = /pie|donut|gauge|funnel/.test(chartType0);
  if (/\b(merge|combine)\b[^.]*\b(series|lines?|bars?|into one)\b/.test(t) || /\bstop (splitting|colou?ring|breaking)\b/.test(t) || /\b(one|single) (line|series|colou?r)\b/.test(t) && /\b(merge|combine|just|only)\b/.test(t)) {
    return { kind: 'viz', vizPatch: { colorBy: null, combineSeries: true }, label: 'Combined into one series' };
  }
  const splitIntent = /\b(separate|split|split by|break ?down by|break (?:them |it |the [a-z]+ )?out(?: by)?|one (?:line|bar|series) per|colou?r(?:ed)? (?:lines?|bars?|series|by)|colou?r by|as separate|different colou?rs?)\b/.test(t);
  if (splitIntent) {
    if (isPieLike) {
      return { kind: 'cannot', label: 'Can’t split a pie/gauge into series',
        message: `A ${chartType0.replace('_', ' ')} can’t show separate coloured series. Switch to a bar, column or line chart first, then split by a field.` };
    }
    const dims = ctx.availableDims || [];
    const curDims = ctx.currentDims || [];
    // The field named after "by/per/into" ("colour BY line of business",
    // "one line PER year"); fall back to the whole phrase.
    const nameSeg = segAfter(t, /\b(?:by|per|into)\s+/, null) || t;
    // Resolve against the CURRENT dimensions first — that's an instant colour-by
    // (works in SQL mode too, since we already have the rows). Match on the
    // trailing field phrase so "colour by …" doesn't defeat the substring test.
    let f = matchField(nameSeg, curDims) || matchField(t, curDims);
    if (f) {
      return { kind: 'viz', vizPatch: { colorBy: f.name, combineSeries: false }, label: `Split by ${f.label || f.name}` };
    }
    // Not in the current result but a known model dimension → re-pull to regroup.
    let g = matchField(nameSeg, dims) || matchField(t, dims);
    if (g) {
      return { kind: 'requery', queryPatch: { addField: g.name, addKind: 'dimension' },
        vizPatch: { colorBy: g.name, combineSeries: false }, label: `Split by ${g.label || g.name}` };
    }
    // No named field: split by an existing non-x dimension if one exists.
    const cur = curDims;
    if (cur.length >= 2) {
      const dateDim = cur.find(d => /date|year|month|day|week|quarter|_dt$|book_end/i.test(d.name || d));
      const splitDim = (dateDim ? cur.find(d => (d.name || d) !== (dateDim.name || dateDim)) : cur[cur.length - 1]);
      const nm = splitDim?.name || splitDim;
      if (nm) return { kind: 'viz', vizPatch: { colorBy: nm, combineSeries: false }, label: `Separate coloured series` };
    }
    return { kind: 'cannot', label: 'Nothing to split by',
      message: 'There’s only one grouping in this result. Tell me a field to split by — e.g. “colour by line of business”.' };
  }

  // Explicit FIELD changes (swap/replace/remove/add) are resolved FIRST, so a
  // field name like "line of business" is never mistaken for a "line chart".
  const fm = parseFieldManipulation(t, ctx);
  if (fm === 'ROUTE_AI') return null;
  if (fm) return fm;

  // A leading verb/keyword that signals "adjust the current result" rather
  // than "ask something new". Keeps us from hijacking genuine questions.
  const looksLikeAdjustment = /(make it|make the|change it|change to|show it|show as|show me (it|as|the)|turn it|turn into|as an?|switch to|display as|hide|show|sort|order|rank|colou?r|palette|legend|grid|label|values?|stack|group|top |horizontal|vertical|by (day|week|month|quarter|year)|daily|weekly|monthly|quarterly|yearly|annual|limit|first |remove|drop|without|add |break ?down|split by|donut|doughnut|\bpie\b|\bline\b|column|\barea\b|scatter|bubble|combo|treemap|funnel|waterfall|heat ?map|gauge|\btable\b|\bgrid\b|bars?|blue|green|red|orange|purple|violet|teal|pink|yellow|cyan|indigo|warm|cool|earth|monochrome|gray|grey|axis|million|thousand|round|whole number|abbreviat|compact|log ?scale|linear scale|from zero|at zero|percent|%|average line|avg line|mean line|target line|reference line|ref line|highlight|emphasi|focus on|dashed|dotted|solid|separate|split|merge|combine)/.test(t);
  if (!looksLikeAdjustment) return null;

  // Does the message reference a model field that is NOT already in the current
  // result? If so it's likely a NEW question / re-query — we must NOT silently
  // reinterpret it as a mere chart tweak. Pure-viz ops are skipped; only an
  // explicit add/remove (re-query) is allowed to proceed below.
  const currentNames = new Set([...(ctx.currentDims || []), ...(ctx.currentMeasures || [])].map(x => (x.name || x)));
  const refField = matchField(t, [...(ctx.availableDims || []), ...(ctx.availableMeasures || [])]);
  const refsNewField = !!(refField && !currentNames.has(refField.name));

  const vizPatch = {};
  let label = '';

  // A distinctive chart/table phrase is honoured even if a chart word happens to
  // fuzzy-match a field name (so "change to a pie chart" can't be forced into an
  // AI re-query by the refsNewField guard).
  if (!refsNewField || VIZ_TARGET_RE.test(t)) {
  // ── chart type / table ──────────────────────────────────────────────
  for (const c of CHART_TYPE_WORDS) {
    if (c.re.test(t)) {
      if (c.outputType) vizPatch.outputType = c.outputType;
      else { vizPatch.chartType = c.chartType; vizPatch.outputType = 'chart'; }
      if (c.extra) Object.assign(vizPatch, c.extra);
      label = c.label;
      break;
    }
  }

  // ── legend ──────────────────────────────────────────────────────────
  if (/\b(hide|remove|no|without)\b[^.]*\blegend\b/.test(t)) { vizPatch.showLegend = false; label ||= 'Legend hidden'; }
  else if (/\b(show|with|display)\b[^.]*\blegend\b/.test(t)) { vizPatch.showLegend = true; label ||= 'Legend shown'; }

  // ── gridlines ───────────────────────────────────────────────────────
  if (/\b(hide|remove|no|without)\b[^.]*\bgrid(lines)?\b/.test(t)) { vizPatch.showGrid = false; label ||= 'Gridlines hidden'; }
  else if (/\b(show|with|display)\b[^.]*\bgrid(lines)?\b/.test(t)) { vizPatch.showGrid = true; label ||= 'Gridlines shown'; }

  // ── data labels ─────────────────────────────────────────────────────
  if (/\b(hide|remove|no|without)\b[^.]*\b(data )?labels?\b/.test(t)) { vizPatch.dataLabels = false; label ||= 'Labels hidden'; }
  else if (/\b(show|with|display|add)\b[^.]*\b((data )?labels?|values)\b/.test(t)) { vizPatch.dataLabels = true; label ||= 'Labels shown'; }

  // ── colours / palette ───────────────────────────────────────────────
  for (const [w, pal] of Object.entries(PALETTE_WORDS)) {
    if (new RegExp(`\\b${w}\\b`).test(t)) { vizPatch.palette = pal; vizPatch.colors = null; label ||= `${w[0].toUpperCase()}${w.slice(1)} palette`; break; }
  }
  if (vizPatch.palette === undefined) {
    for (const [w, hex] of Object.entries(COLOR_WORDS)) {
      if (new RegExp(`\\b${w}\\b`).test(t)) { vizPatch.colors = [hex]; label ||= `${w[0].toUpperCase()}${w.slice(1)} colour`; break; }
    }
  }

  // ── orientation ─────────────────────────────────────────────────────
  if (/\bhorizontal\b/.test(t)) { vizPatch.orientation = 'horizontal'; label ||= 'Horizontal bars'; }
  else if (/\bvertical\b/.test(t)) { vizPatch.orientation = 'vertical'; label ||= 'Vertical bars'; }

  // ── stacking ────────────────────────────────────────────────────────
  if (/\b(100%|percent stack|normali[sz]ed)\b/.test(t)) { vizPatch.barLayout = 'percent'; vizPatch.stacked = true; label ||= '100% stacked'; }
  else if (/\bstack(ed)?\b/.test(t)) { vizPatch.barLayout = 'stacked'; vizPatch.stacked = true; label ||= 'Stacked'; }
  else if (/\b(group(ed)?|unstack(ed)?|side by side)\b/.test(t)) { vizPatch.barLayout = 'grouped'; vizPatch.stacked = false; label ||= 'Grouped'; }

  // ── sort ────────────────────────────────────────────────────────────
  if (/\bsort|order|rank|highest|lowest|largest|smallest|biggest|ascending|descending|desc|asc\b/.test(t)) {
    if (/\b(alphabetical|by name|a-?z|z-?a)\b/.test(t)) {
      vizPatch.sort = { by: 'dimension', dir: /z-?a|reverse|descending|desc/.test(t) ? 'desc' : 'asc' };
      label ||= 'Sorted A–Z';
    } else if (/\b(asc|ascending|lowest|smallest)\b/.test(t)) {
      vizPatch.sort = { by: 'measure', dir: 'asc' }; label ||= 'Sorted ascending';
    } else if (/\b(desc|descending|highest|largest|biggest|top to bottom)\b/.test(t) || /\bsort|order|rank\b/.test(t)) {
      vizPatch.sort = { by: 'measure', dir: 'desc' }; label ||= 'Sorted descending';
    }
  }

  // ── display top-N (client slice) vs re-query limit ──────────────────
  const topMatch = t.match(/\btop\s+(\d{1,4})\b/);
  if (topMatch) { vizPatch.topN = parseInt(topMatch[1], 10); label ||= `Top ${topMatch[1]}`; }
  if (/\b(show all|all rows|everything)\b/.test(t)) { vizPatch.topN = null; label ||= 'All rows'; }

  // ── axis titles ─────────────────────────────────────────────────────
  if (/\b(hide|remove|no|without)\b[^.]*\b(axis (titles?|labels?))\b/.test(t)) { vizPatch.showAxisTitles = false; label ||= 'Axis titles hidden'; }
  else if (/\b(show|with|display|add)\b[^.]*\b(axis (titles?|labels?))\b/.test(t)) { vizPatch.showAxisTitles = true; label ||= 'Axis titles shown'; }

  // ── number & date display ───────────────────────────────────────────
  if (/\bin millions\b|\bas millions\b/.test(t)) { vizPatch.numberFormat = 'millions'; label ||= 'Shown in millions'; }
  else if (/\bin thousands\b|\bas thousands\b/.test(t)) { vizPatch.numberFormat = 'thousands'; label ||= 'Shown in thousands'; }
  else if (/\b(round(ed)?|whole numbers?|no decimals?)\b/.test(t)) { vizPatch.numberFormat = 'whole'; label ||= 'Rounded to whole numbers'; }
  else if (/\b(abbreviate|shorten|compact|abbreviated)\b/.test(t)) { vizPatch.numberFormat = 'abbreviate'; label ||= 'Abbreviated numbers'; }
  else if (/\bfull numbers?\b|\bexact numbers?\b/.test(t)) { vizPatch.numberFormat = 'full'; label ||= 'Full numbers'; }

  // ── axis scale ──────────────────────────────────────────────────────
  if (/\b(start (the )?(y ?)?axis at zero|from zero|baseline (at )?zero|zero baseline)\b/.test(t)) { vizPatch.axisFromZero = true; label ||= 'Axis starts at zero'; }
  if (/\blog(arithmic)? scale\b/.test(t)) { vizPatch.logScale = true; label ||= 'Log scale'; }
  else if (/\blinear scale\b/.test(t)) { vizPatch.logScale = false; label ||= 'Linear scale'; }
  if (/\b(as )?(a )?(percent|%)\s*of\s*total\b/.test(t)) { vizPatch.barLayout = 'percent'; vizPatch.stacked = true; label ||= '% of total'; }

  // ── reference / target / average line ───────────────────────────────
  if (/\b(add|show|draw)\b[^.]*\b(average|avg|mean)\s*line\b/.test(t)) { vizPatch.refLine = { mode: 'avg' }; label ||= 'Average line'; }
  const _tgt = t.match(/\btarget\s*line\s*(?:at|of|=)?\s*\$?([\d][\d,]*\.?\d*)/) || t.match(/\b(?:reference|ref)\s*line\s*(?:at|of|=)?\s*\$?([\d][\d,]*\.?\d*)/);
  if (_tgt) { vizPatch.refLine = { mode: 'target', value: parseFloat(_tgt[1].replace(/,/g, '')) }; label ||= `Target line at ${_tgt[1]}`; }
  if (/\b(remove|hide|no)\b[^.]*\b(average|avg|target|reference|ref)\s*line\b/.test(t)) { vizPatch.refLine = { mode: 'none' }; label ||= 'Reference line removed'; }

  // ── series emphasis ─────────────────────────────────────────────────
  if (/\bdashed\b/.test(t)) { vizPatch.lineStyle = 'dashed'; label ||= 'Dashed line'; }
  else if (/\bdotted\b/.test(t)) { vizPatch.lineStyle = 'dotted'; label ||= 'Dotted line'; }
  else if (/\bsolid\b/.test(t)) { vizPatch.lineStyle = 'solid'; label ||= 'Solid line'; }
  const _hl = t.match(/\b(highlight|emphasi[sz]e|focus on)\s+([a-z0-9][a-z0-9 &/-]{0,40}?)\s*$/);
  if (_hl) { vizPatch.highlightValue = _hl[2].trim(); label ||= `Highlighted ${_hl[2].trim()}`; }
  if (/\b(clear|remove) (the )?highlight\b|\bunhighlight\b/.test(t)) { vizPatch.highlightValue = null; label ||= 'Highlight cleared'; }
  } // end !refsNewField (pure-viz ops)

  // If we captured any presentation change, it's a pure viz op.
  if (Object.keys(vizPatch).length > 0) return { kind: 'viz', vizPatch, label: label || 'View updated' };

  // ── deterministic RE-QUERY ops (need a backend query, not the LLM) ──
  const queryPatch = {};
  let rlabel = '';

  // date grain (only meaningful if the current result has a date dimension
  // AND the message isn't introducing a different field → a new question)
  const grainMatch = t.match(/\b(by\s+)?(day|daily|week|weekly|month|monthly|quarter|quarterly|year|yearly|annually|annual)\b/);
  if (grainMatch && ctx.hasDateDim && !refsNewField) {
    queryPatch.dateGrain = GRAIN_WORDS[grainMatch[2]];
    rlabel = `By ${queryPatch.dateGrain}`;
  }

  // row limit
  const limMatch = t.match(/\b(limit|first|fetch|load|show)\s+(\d{2,5})\s*(rows|records|results)?\b/);
  if (limMatch && parseInt(limMatch[2], 10) > 0) { queryPatch.limit = parseInt(limMatch[2], 10); rlabel ||= `Limit ${limMatch[2]}`; }

  if (Object.keys(queryPatch).length > 0) return { kind: 'requery', queryPatch, label: rlabel || 'Re-queried' };

  return null;
}
